/**
 * DiscountField
 * -------------
 * Enter a discount code and see what it does to the price, before paying.
 *
 * The amount shown here is the server's answer, never this component's: it
 * posts the code and the gross amount and renders what comes back. A client
 * that computed its own total would be quoting a number the buyer chose, and
 * the payment routes would have to trust it — so they don't, and neither does
 * this.
 *
 * Checking does not reserve the code. The use is spent when the payment
 * settles, so a client can try a code, change their mind, and still have it.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert, Box, Button, Collapse, Stack, TextField, Typography,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { discountsApi } from '@/services/api';

export default function DiscountField({
  amountToman, context = 'all', clinicCode, onChange, t, lang = 'fa',
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);

  const money = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(n || 0);

  const checkMut = useMutation({
    mutationFn: () => discountsApi.check({
      code: code.trim().toUpperCase(),
      amountToman,
      context,
      clinicCode,
    }),
    onSuccess: (res) => {
      setResult(res);
      // Hand the parent only the CODE. The final figure is re-derived by the
      // server at payment; passing a price up would invite someone to send it.
      onChange?.(res?.valid ? code.trim().toUpperCase() : null);
    },
    onError: () => {
      setResult({ valid: false, reason: 'ERROR' });
      onChange?.(null);
    },
  });

  const clear = () => {
    setCode(''); setResult(null); onChange?.(null);
  };

  if (!open) {
    return (
      <Button size="small" startIcon={<LocalOfferIcon />} onClick={() => setOpen(true)}
        sx={{ alignSelf: 'flex-start' }}>
        {t('discount.have')}
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small" fullWidth autoFocus
          label={t('discount.label')}
          value={code}
          disabled={Boolean(result?.valid)}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) checkMut.mutate(); }}
          inputProps={{ style: { letterSpacing: '.08em' } }}
        />
        {result?.valid ? (
          <Button size="small" onClick={clear} sx={{ mt: 0.5 }}>
            {t('discount.remove')}
          </Button>
        ) : (
          <Button size="small" variant="outlined" sx={{ mt: 0.5 }}
            disabled={!code.trim() || checkMut.isPending}
            onClick={() => checkMut.mutate()}>
            {t('discount.apply')}
          </Button>
        )}
      </Stack>

      <Collapse in={Boolean(result)}>
        {result?.valid ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={700}>
              {t('discount.applied', { amount: money(result.discountToman) })}
            </Typography>
            {/* The gross price is printed directly above this field, so a
                struck-through copy here only crowded the line. */}
            <Typography variant="body2">
              {t('discount.newTotal', { amount: money(result.finalToman) })}
            </Typography>
          </Alert>
        ) : result ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {/* Named reasons, because "invalid code" makes a client retype a
                code that was never going to work — they need to know whether
                to fix it or give up. */}
            {t(`discount.reason.${result.reason}`, t('discount.reason.NOT_FOUND'))}
          </Alert>
        ) : null}
      </Collapse>
    </Box>
  );
}
